import { Search, MapPin, Calendar, Users, Home, DollarSign, Filter, X } from 'lucide-react';
import { FilterState } from '@/types/property';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useState, memo } from 'react';
import DatePicker from './DatePicker';

interface FilterBarProps {
    filters: FilterState;
    onFilterChange: (filters: FilterState) => void;
    propertyTypes: Array<{ _id: string; name: string; externalId?: string }>;
    areaList: Array<{ _id: string; name: string; externalId?: string }>;
    amenitiesList: Array<{ _id: string; name: string; externalId?: string }>;
}

function FilterBarComponent({ filters, onFilterChange, propertyTypes, areaList, amenitiesList }: FilterBarProps) {
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

    const hasActiveFilters = () => {
        return !!(filters.area ||
            (filters.minPrice !== '' && filters.minPrice !== undefined) ||
            (filters.maxPrice !== '' && filters.maxPrice !== undefined) ||
            filters.bedrooms ||
            filters.bathrooms ||
            filters.amenities.length > 0 ||
            (filters.guests && filters.guests > 0));
    };

    const clearAllFilters = () => {
        onFilterChange({
            ...filters,
            area: '',
            minPrice: '',
            maxPrice: '',
            bedrooms: undefined,
            bathrooms: undefined,
            guests: 0,
            amenities: []
        });
    };

    return (
        <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="container mx-auto px-6 py-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                    <div className="lg:col-span-2">
                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                            Search
                        </Label>
                        <Input
                            placeholder="Search by name"
                            value={filters.search}
                            onChange={(e) => onFilterChange({ ...filters, search: e.target.value })}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all text-black"
                        />
                    </div>

                    <div className="lg:col-span-1">
                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                            Area (Location)
                        </Label>

                        <Select
                            value={filters.area || ""}
                            onValueChange={(value) =>
                                onFilterChange({ ...filters, area: value })
                            }
                        >
                            <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus-visible:border focus-visible:border-secondary focus:ring-emerald-500 transition-all bg-white cursor-pointer">
                                <SelectValue placeholder="All areas" />
                            </SelectTrigger>

                            <SelectContent>
                                {/* <SelectItem value="all_areas">All Areas</SelectItem> */}
                                {areaList?.map((area) => (
                                    <SelectItem
                                        key={area.externalId || area._id}
                                        value={String(area.externalId || area._id)}
                                    >
                                        {area.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>


                    <div className="lg:col-span-1">
                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                            Check in & Check out
                        </Label>
                        <DatePicker
                            value={{ checkIn: filters.checkin, checkOut: filters.checkout }}
                            onChange={(dates) => onFilterChange({
                                ...filters,
                                checkin: dates.checkIn,
                                checkout: dates.checkOut
                            })}
                            placeholder="Add dates"
                            fieldType="both"
                        />
                    </div>

                    <div className="lg:col-span-1">
                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                            Max Guests
                        </Label>
                        <Select
                            value={filters.guests ? filters.guests.toString() : ""}
                            onValueChange={(value) => onFilterChange({ ...filters, guests: Number(value) })}
                        >
                            <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer">
                                <SelectValue placeholder="Any number" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">1 guest</SelectItem>
                                <SelectItem value="2">2 guests</SelectItem>
                                <SelectItem value="3">3 guests</SelectItem>
                                <SelectItem value="4">4 guests</SelectItem>
                                <SelectItem value="5">5 guests</SelectItem>
                                <SelectItem value="6">6 guests</SelectItem>
                                <SelectItem value="7">7 guests</SelectItem>
                                <SelectItem value="8">8 guests</SelectItem>
                                <SelectItem value="9">9 guests</SelectItem>
                                <SelectItem value="10">10 guests</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="lg:col-span-1">
                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Type
                        </Label>
                        <Select
                            value={filters.type}
                            onValueChange={(value) => onFilterChange({ ...filters, type: value })}
                        >
                            <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus-visible:border focus-visible:border-secondary focus:ring-emerald-500 transition-all bg-white cursor-pointer">
                                <SelectValue placeholder="All types" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Any Type</SelectItem>
                                {propertyTypes.map((propertyType: { _id: string; name: string; externalId?: string }, idx: number) => (
                                    <SelectItem key={idx} value={propertyType.externalId || propertyType._id}>{propertyType.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="lg:col-span-1 flex items-end">
                        <Dialog open={isFilterDialogOpen} onOpenChange={setIsFilterDialogOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="w-full">
                                    <Filter className="w-4 h-4 mr-2" />
                                    Filters
                                    {hasActiveFilters() && (
                                        <span className="ml-2 bg-red-600 text-white text-xs rounded-full px-2 py-1">
                                            {[filters.area, filters.minPrice, filters.maxPrice, filters.bedrooms, filters.bathrooms, filters.amenities.length, (filters.guests ?? 0) > 0 ? 1 : 0].filter(Boolean).length}
                                        </span>
                                    )}
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                    <DialogTitle className="flex items-center gap-4">
                                        <span>Additional Filters</span>
                                    </DialogTitle>
                                </DialogHeader>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                                    {/* <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Min Price (AED)
                                        </Label>
                                        <Input
                                            type="number"
                                            placeholder="Min"
                                            value={filters.minPrice === '' || filters.minPrice === undefined ? '' : String(filters.minPrice)}
                                            onChange={(e) => onFilterChange({ ...filters, minPrice: e.target.value === '' ? '' : Number(e.target.value) })}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                        />
                                    </div> */}

                                    {/* <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Max Price (AED)
                                        </Label>
                                        <Input
                                            type="number"
                                            placeholder="Max"
                                            value={filters.maxPrice === '' || filters.maxPrice === undefined ? '' : String(filters.maxPrice)}
                                            onChange={(e) => onFilterChange({ ...filters, maxPrice: e.target.value === '' ? '' : Number(e.target.value) })}
                                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                                        />
                                    </div> */}

                                    {/* <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Bedrooms
                                        </Label>
                                        <Select
                                            value={filters.bedrooms ?? ''}
                                            onValueChange={(value) => onFilterChange({ ...filters, bedrooms: value === 'any' ? undefined : value })}
                                        >
                                            <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer">
                                                <SelectValue placeholder="Any" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="any">Any</SelectItem>
                                                <SelectItem value="studio">Studio</SelectItem>
                                                {Array.from({ length: 20 }, (_, i) => String(i + 1)).map((n) => (
                                                    <SelectItem key={n} value={n}>{n}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div> */}

                                    {/* <div>
                                        <Label className="block text-sm font-medium text-gray-700 mb-2">
                                            Bathrooms
                                        </Label>
                                        <Select
                                            value={filters.bathrooms ?? ''}
                                            onValueChange={(value) => onFilterChange({ ...filters, bathrooms: value === 'any' ? undefined : value })}
                                        >
                                            <SelectTrigger className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all appearance-none bg-white cursor-pointer">
                                                <SelectValue placeholder="Any" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="any">Any</SelectItem>
                                                <SelectItem value="none">None</SelectItem>
                                                {Array.from({ length: 20 }, (_, i) => String(i + 1)).map((n) => (
                                                    <SelectItem key={n} value={n}>{n}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div> */}

                                    <div className="md:col-span-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <Label className="block text-lg font-medium text-gray-700 mb-2">
                                                Amenities
                                            </Label>
                                            {hasActiveFilters() && (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    onClick={clearAllFilters}
                                                    className="text-red-600 hover:text-red-700 underline"
                                                >
                                                    {/* <X className="w-4 h-4 mr-1" /> */}
                                                    Clear All
                                                </Button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            {amenitiesList.map((amenity, idx) => (
                                                <div key={idx} className="flex items-center gap-2">
                                                    <Input
                                                        type="checkbox"
                                                        placeholder="wifi, parking, pool"
                                                        value={amenity.externalId || amenity._id}
                                                        checked={filters.amenities.includes(amenity.externalId || amenity._id)}
                                                        onChange={(e) => {
                                                            const id = amenity.externalId || amenity._id;
                                                            const updated = e.target.checked
                                                                ? [...filters.amenities, id]
                                                                : filters.amenities.filter(a => a !== id);
                                                            onFilterChange({ ...filters, amenities: updated });
                                                        }}
                                                        className="h-5 w-5 shrink-0"
                                                    />
                                                    <Label className="block text-sm font-medium text-gray-700">
                                                        {amenity.name}
                                                    </Label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                                    <Button
                                        variant="outline"
                                        onClick={() => setIsFilterDialogOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={() => setIsFilterDialogOpen(false)}
                                        className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                        Apply Filters
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default memo(FilterBarComponent);

